import { Logger } from '@nextgen-web-framework/core';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';

import {
  profileApi,
  ProfilePhoneEmailEnum,
  type DynamicObjectInterface,
  type AlternativeContactType,
  type CreateChallengeRequest,
  type CreateAccountChallengeAPIRequest,
  type FunctionType,
} from '@commerce-profile-webcomponents/sdk';
import { AlternativeContactCallbackProps } from '../components/AccountInformation/AccountInformationEdit/types/propTypes';
import { handleAPIResponseError } from './profileUtils';
import { defaultApiErrorHandler, parsePhoneNumber, parseString, removePhoneLeadingZero } from './helper';
import { HandleVerifyContactProps, ProfileEmailProcessType, ProfilePhoneProcessType } from './types/propTypes';
import { conditionalError } from './myAccountProfileUtils';
import { ErrorConstants } from './Constants';

/* Purpose: Common contact functions */

export const processEmailField = (
  meta: DynamicObjectInterface,
  styles: DynamicObjectInterface,
  data: ProfileEmailProcessType,
) => {
  const { emailVerified, isEmailInUsed, email, originalEmail } = data;
  const isEmailValid = meta.touched && !meta.error;
  const emailChanged = (email ?? '').trim() !== (originalEmail ?? '').trim();
  const emailRequireOTP = email && emailChanged && isEmailValid && !emailVerified;
  const className = emailRequireOTP ? styles['blue'] : styles['grey'];
  let forceError = false;

  let errMsg = meta.error;
  if (isEmailInUsed) {
    errMsg = 'profile.validationMsg.email.inused';
    forceError = true;
  }
  return {
    isEmailValid,
    emailRequireOTP,
    className,
    errMsg,
    forceError,
  };
};

export const processPhoneField = (
  meta: DynamicObjectInterface,
  styles: DynamicObjectInterface,
  data: ProfilePhoneProcessType,
) => {
  const { phoneVerified, isPhoneInUsed, phone, dialCode, originalPhone, originalDialCode } = data;
  const formattedPhone = parsePhoneNumber(dialCode, phone);
  const isPhoneFormatValid = isValidPhoneNumber(formattedPhone);
  const isPhoneErrorFree = dialCode && phone ? isPhoneFormatValid : true;
  const isPhoneValid = meta.touched && !meta.error && isPhoneErrorFree;
  let forceError = false;

  let errMsg = conditionalError(!isPhoneFormatValid, 'profile.validationMsg.phone.format');
  if (isPhoneInUsed) {
    errMsg = 'profile.validationMsg.phone.inused';
    forceError = true;
  } else if (meta.touched && !dialCode && phone) {
    errMsg = 'profile.validationMsg.dialCode.required';
  } else if (meta.error) {
    errMsg = meta.error || '';
  }

  const dialCodeValue = dialCode.replace(/[()]/g, '');
  const originalDialCodeValue = originalDialCode?.replace(/[()]/g, '');

  const phoneRequireOTP =
    dialCode &&
    phone &&
    isPhoneValid &&
    !phoneVerified &&
    (removePhoneLeadingZero(parseString(phone)) !== removePhoneLeadingZero(parseString(originalPhone)) ||
      dialCodeValue !== originalDialCodeValue);

  const className = phoneRequireOTP ? styles['blue'] : styles['grey'];

  return {
    className,
    phoneRequireOTP,
    errMsg,
    isPhoneValid,
    forceError,
  };
};

export const processAltContact = (data: AlternativeContactCallbackProps, prevProfile: AlternativeContactType) => {
  const { contact } = data;
  const currentContacts: AlternativeContactType[] = prevProfile?.profileData?.alternativePhone ?? [];

  let updatedAltContacts: AlternativeContactType[] = [];

  const indexToUpdate = currentContacts.findIndex((item: AlternativeContactType) => item.id === contact.id);

  if (indexToUpdate !== -1) {
    // Update the contact if found
    const updatedContacts = [...currentContacts];
    updatedContacts[indexToUpdate] = contact; // Replace the contact with the updated one
    updatedAltContacts = updatedContacts;
  }

  return updatedAltContacts;
};

export const generateChallengeId = async (
  payload: CreateAccountChallengeAPIRequest,
  useAccountApiForContacts = false,
  apiErrorHandler = defaultApiErrorHandler,
) =>
  useAccountApiForContacts
    ? await apiErrorHandler(() => profileApi.postAccountChallenge(payload))
    : await apiErrorHandler(() => profileApi.postProfileChallange(payload as DynamicObjectInterface));

export const handleContactVerification = async (
  payload: CreateAccountChallengeAPIRequest,
  setShowOTPVerification: FunctionType,
  setChallengeId: FunctionType,
  setEmailInUsed: FunctionType,
  setPhoneInUsed: FunctionType,
  useAccountApiForContacts: boolean,
) => {
  const logger = Logger.getInstance();
  try {
    const challengeRes = await generateChallengeId(payload, useAccountApiForContacts);
    setShowOTPVerification(true);
    setChallengeId(challengeRes?.challengeId);
  } catch (err) {
    const errorCode = handleAPIResponseError(err);
    if ([ErrorConstants.EMAIL_NOT_UNIQUE, ErrorConstants.ACCOUNT_EMAIL_NOT_UNIQUE].includes(errorCode)) {
      setEmailInUsed(true);
    } else if ([ErrorConstants.PHONE_NOT_UNIQUE, ErrorConstants.ACCOUNT_PHONE_NOT_UNIQUE].includes(errorCode)) {
      setPhoneInUsed(true);
    } else {
      logger.error(err);
    }
  }
};

export const handleVerifyContactCTA = ({ data, funcs }: HandleVerifyContactProps) => {
  const {
    dataType,
    labelVerify,
    requiredVerify,
    currentValue,
    currentDialCode,
    profileId,
    accountNum,
    useAccountApiForContacts,
  } = data;
  const {
    setVerificationType,
    setVerificationValue,
    setShowOTPVerification,
    setChallengeId,
    setEmailInUsed,
    setPhoneInUsed,
  } = funcs;

  const isPhone = dataType === ProfilePhoneEmailEnum.PHONE;

  const text = labelVerify;
  const id = `btn_${dataType}_verify`;
  const value = isPhone ? parsePhoneNumber(currentDialCode ?? '', currentValue) : currentValue;

  const challengeReq: CreateChallengeRequest = {
    [dataType === ProfilePhoneEmailEnum.EMAIL ? 'email' : 'phone']: value,
    profileId,
  };
  const payload: CreateAccountChallengeAPIRequest = {
    accountNum,
    profileId,
    payload: challengeReq,
  };
  const canVerify = requiredVerify && profileId;
  return {
    text,
    id,
    ariaLabel: id,
    onClick: () => {
      if (canVerify) {
        setVerificationType(dataType);
        setVerificationValue(value);
        handleContactVerification(
          payload,
          setShowOTPVerification,
          setChallengeId,
          setEmailInUsed,
          setPhoneInUsed,
          useAccountApiForContacts,
        );
      }
    },
  };
};
